// Choice families: several choices of one action kind that differ only in their step bounds (for
// example one offer per combination of resources given) are shown as one form. The form never
// invents a move: it submits the first offered choice that accepts every answer, through the same
// draft model as any other choice, and the host still judges the command. A package opts action kinds
// in with `interactions.forms`; a single choice of such a kind also opens as this form.

import { answer, assemble, currentStep, startDraft, validateStep, type ChoiceView, type Draft } from '../model/choices';
import type { AllocateKey, PickOption, Scalar } from '../protocol/ui';

export type FamilyField =
  | { type: 'pick'; field: string; label: string; options: PickOption[] }
  | { type: 'allocate'; field: string; label: string; keys: AllocateKey[] };

export interface Family { kind: string; fields: FamilyField[]; choices: ChoiceView[] }

export interface FamilyValues { [field: string]: Scalar | number[] | undefined }

/** The family these choices form, or null when they are not interchangeable variants of one form. */
export function choiceFamily(choices: ChoiceView[]): Family | null {
  if (!choices.length) return null;
  const kind = choices[0]!.action.kind;
  const fields = new Map<string, FamilyField>();
  let signature: string | null = null;
  for (const c of choices) {
    if (c.action.kind !== kind || c.parameters.length || !c.steps.length) return null;
    const sig = c.steps.map(s => `${s.type}:${s.field}`).join('|');
    if (signature !== null && sig !== signature) return null;
    signature = sig;
    for (const s of c.steps) {
      if (s.type === 'pick') {
        // Picks among table objects stay on the table; a form offers only plain options.
        if ((s.max ?? 1) !== 1 || s.ordered || s.expand || s.options.some(o => o.then?.length || o.objectId)) return null;
        const f = fields.get(s.field) as Extract<FamilyField, { type: 'pick' }> | undefined;
        if (!f) { fields.set(s.field, { type: 'pick', field: s.field, label: s.label, options: [...s.options] }); continue; }
        for (const o of s.options) if (!f.options.some(x => x.value === o.value)) f.options.push(o);
      } else if (s.type === 'allocate') {
        if (s.encoding !== 'array') return null;
        const f = fields.get(s.field) as Extract<FamilyField, { type: 'allocate' }> | undefined;
        if (!f) { fields.set(s.field, { type: 'allocate', field: s.field, label: s.label, keys: s.keys.map(k => ({ ...k })) }); continue; }
        if (f.keys.length !== s.keys.length || f.keys.some((k, i) => k.key !== s.keys[i]!.key)) return null;
        f.keys = f.keys.map((k, i) => {
          const o = s.keys[i]!, available = k.available === undefined ? o.available : o.available === undefined ? k.available : Math.max(k.available, o.available);
          return { ...k, min: Math.min(k.min, o.min), max: Math.max(k.max, o.max), ...(available === undefined ? {} : { available }) };
        });
      } else return null;
    }
  }
  return fields.size && [...fields.values()].some(f => f.type === 'allocate') ? { kind, fields: [...fields.values()], choices } : null;
}

export function initialValues(family: Family): FamilyValues {
  const out: FamilyValues = {};
  for (const f of family.fields) out[f.field] = f.type === 'allocate' ? f.keys.map(() => 0) : f.options.length === 1 ? f.options[0]!.value : undefined;
  return out;
}

/** The first choice whose every step accepts these values, as a complete draft; else the first problem met. */
export function matchFamily(family: Family, values: FamilyValues): { choice: ChoiceView; draft: Draft } | { problem: string } {
  // The variant that got furthest explains what is missing (the one whose give matches, say).
  let problem: string | null = null, depth = -1;
  for (const choice of family.choices) {
    let draft = startDraft(choice);
    try {
      for (let step = currentStep(draft); step; step = currentStep(draft)) {
        const value = values[step.field];
        if (value === undefined) throw new Error(`Choose ${step.label.toLowerCase()}.`);
        const error = validateStep(step, value);
        if (error) throw new Error(error);
        draft = answer(draft, value);
      }
      assemble(draft);
      return { choice, draft };
    } catch (e) { if (draft.answers.length > depth) { depth = draft.answers.length; problem = (e as Error).message; } }
  }
  return { problem: problem ?? 'No offered move matches.' };
}
